/**
 * QueryService API endpoints
 *
 * For static export, these endpoints call the backend API directly
 * using the /api/v1/ prefix. Status values are normalized client-side.
 */
import {
  QpsWarningResponse,
  QueryService,
  QueryServiceActionResponse,
  QueryServiceFormData,
  QueryServiceListParams,
  QueryServiceListResponse,
} from '@/features/queryservices/types/queryservice';
import apiClient, { QueryParams } from './client';
import { ApiDeleteResponse } from './types';

// Base URL for query services API - single source of truth
export const QUERY_SERVICES_API_BASE = '/api/v1/queryservices';

/**
 * Normalize status to match expected format (capitalize first letter)
 * Backend may return lowercase status like "running", UI expects "Running"
 */
function normalizeStatus(status: string): string {
  if (!status) return status;
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

/**
 * Normalize query service data from backend.
 * - Capitalises status (e.g. "running" → "Running")
 * - Flattens the nested `qps` object into `qpsEnabled` / `qpsCount`
 */
function normalizeQueryService(qs: QueryService): QueryService {
  const normalised: QueryService = {
    ...qs,
    status: normalizeStatus(qs.status) as QueryService['status'],
  };

  // Backend returns QPS as a nested object: { qps: { enabled, count } }
  // Flatten into qpsEnabled / qpsCount so the rest of the UI can read them directly
  if (qs.qps && typeof qs.qps === 'object') {
    normalised.qpsEnabled = qs.qps.enabled ?? false;
    normalised.qpsCount = qs.qps.count ?? 0;
  }

  return normalised;
}

export const queryServicesApi = {
  /**
   * List all query services with optional filtering and pagination
   */
  list: async (params?: QueryServiceListParams): Promise<QueryServiceListResponse> => {
    const response = await apiClient.get<QueryServiceListResponse>(QUERY_SERVICES_API_BASE, {
      params: params as QueryParams,
    });
    // Normalize status values in the response
    return {
      ...response,
      items: Array.isArray(response.items)
        ? response.items.map(normalizeQueryService)
        : response.items,
    };
  },

  /**
   * Get a single query service by name
   */
  get: async (name: string): Promise<QueryService> => {
    const response = await apiClient.get<QueryService>(`${QUERY_SERVICES_API_BASE}/${name}`);
    return normalizeQueryService(response);
  },

  /**
   * Create a new query service
   * Returns QpsWarningResponse when capacity is insufficient and ignoreFeedback is false.
   * The ignoreFeedback flag is sent in the JSON body (not as a query param) per backend contract.
   * `operationId` is propagated via X-Operation-Id header for end-to-end correlation.
   */
  create: (
    data: QueryServiceFormData,
    options?: { ignoreFeedback?: boolean; operationId?: string }
  ): Promise<QueryService | QpsWarningResponse> => {
    const body = options?.ignoreFeedback ? { ...data, ignoreFeedback: true } : data;
    return apiClient.post(QUERY_SERVICES_API_BASE, body, { operationId: options?.operationId });
  },

  /**
   * Update an existing query service
   * Returns QpsWarningResponse when capacity is insufficient and ignoreFeedback is false.
   * The ignoreFeedback flag is sent in the JSON body (not as a query param) per backend contract.
   * `operationId` is propagated via X-Operation-Id header for end-to-end correlation.
   */
  update: (
    name: string,
    data: Partial<QueryServiceFormData>,
    options?: { ignoreFeedback?: boolean; operationId?: string }
  ): Promise<QueryService | QpsWarningResponse> => {
    const body = options?.ignoreFeedback ? { ...data, ignoreFeedback: true } : data;
    return apiClient.put(`${QUERY_SERVICES_API_BASE}/${name}`, body, {
      operationId: options?.operationId,
    });
  },

  /**
   * Delete a query service
   *
   * `operationId` reaches compute-api's logs and span, but not the operator:
   * a delete cannot carry the e6.io/operation-id annotation, because the
   * object it would be written on is the one being removed. So this correlates
   * the browser, Envoy and API hops for a delete, and the operator side stays
   * a known gap.
   */
  delete: (name: string, options?: { operationId?: string }): Promise<ApiDeleteResponse> => {
    return apiClient.delete(`${QUERY_SERVICES_API_BASE}/${name}`, {
      operationId: options?.operationId,
    });
  },

  /**
   * Suspend a query service
   * `operationId` is propagated via X-Operation-Id header for end-to-end correlation.
   */
  suspend: (
    name: string,
    options?: { operationId?: string }
  ): Promise<QueryServiceActionResponse> => {
    return apiClient.post(`${QUERY_SERVICES_API_BASE}/${name}/suspend`, undefined, {
      operationId: options?.operationId,
    });
  },

  /**
   * Resume a query service
   * `operationId` is propagated via X-Operation-Id header for end-to-end correlation.
   */
  resume: (
    name: string,
    options?: { operationId?: string }
  ): Promise<QueryServiceActionResponse> => {
    return apiClient.post(`${QUERY_SERVICES_API_BASE}/${name}/resume`, undefined, {
      operationId: options?.operationId,
    });
  },
};

export default queryServicesApi;
